import { useContext, useEffect, useState } from "react";
import { TranslationService } from "../../services/TranslationService";
import { RequiredManager, ValidationMessage } from "../shared/RequieredManager";
import { useHistory } from "react-router-dom";
import AuthenticationService from "../../services/AuthenticationService";
import * as yup from "yup";
import ErrorContext from "../shared/ErrorContext";

type PasswordModelRequest = { password: string }
const defaultValues = { password: "" };

export const PasswordExpired = () => {
    const requiredManager = new RequiredManager();
    const [formData, setFormData] = useState<PasswordModelRequest>(defaultValues);
    const [loading, setLoading] = useState(false);
    const [visiblePassword, setVisiblePassword] = useState(false);
    const [passwordUnchanged, setPasswordUnchanged] = useState(false);
    const [passwordMsgBlocker, setPasswordMsgBlocker] = useState("");

    const { translate, currentLanguage } = TranslationService;
    const { setCurrentError } = useContext(ErrorContext);

    const formDataSchema = yup.object({
        password: yup
            .string()
            .required(translate.LoginPasswordMissing)
            .min(8, translate.PasswordMinimunLength.replace('{{p1}}', '8')),
    });

    const history = useHistory();
    const token = new URLSearchParams(history.location.search).get("token") ?? "";
    const email = new URLSearchParams(history.location.search).get("email") ?? "";
    const culture = currentLanguage || 'es';

    const submit = async () => {
        setCurrentError(undefined);
        setPasswordUnchanged(false);
        setPasswordMsgBlocker("");
        setLoading(true);

        formDataSchema
            .validate(formData)
            .then(async () => {
                setLoading(true);

                const userData = await AuthenticationService.changePasswordExpired(
                    {
                        password: formData.password!,
                        email,
                        token
                    }
                )

                if (userData instanceof Error) {
                    setCurrentError(translate.Network);
                    return;
                }

                if (userData) {
                    if (userData.response === "Ok") {
                        const queryParams = new URLSearchParams();
                        queryParams.append("msgtr", 'PasswordChanged');
                        history.replace(`/${culture}/logon?${queryParams.toString()}`);
                    } else {
                        setPasswordUnchanged(true);
                        switch (userData.response) {
                            case "invalidtoken":
                                setPasswordMsgBlocker(translate.TokenInvalidPassword);
                                break;
                            case "weak-password":
                                setPasswordUnchanged(false);
                                setCurrentError(translate.PasswordWeakMsg);
                                break;
                            case "userdelegated":
                                setPasswordMsgBlocker(translate.PasswordUserDelegatedMsg);
                                break;
                            case "old-password":
                                setPasswordMsgBlocker(translate.PasswordOldMsg);
                                break;
                            case "wait-password":
                                setPasswordMsgBlocker(translate.PasswordWaitMsg);
                                break;
                            default:
                                setPasswordMsgBlocker(translate.Network);
                                break;
                        }
                    }
                } else {
                    setCurrentError(translate.Network);
                }
            })
            .catch(({ errors }) => setCurrentError(errors[0]))
            .finally(() => setLoading(false));
    };

    const handleInputChange = ({ target: { name, value, checked, type } }: React.ChangeEvent<HTMLInputElement>) =>
        setFormData((prevState) => ({
            ...prevState,
            [name]: type === "checkbox" ? checked : value,
        }));

    const handleVisiblePassword = () =>
        setVisiblePassword((prevState) => !prevState);

    useEffect(() => {
        setCurrentError(undefined);
        setPasswordUnchanged(false);
        setPasswordMsgBlocker("");

        if (!(email && token)) {
            history.push(`${currentLanguage}/logon`);
        }
    }, [email, token, currentLanguage, history, setCurrentError]);

    return (
        <form onSubmit={submit} noValidate>
            {!passwordUnchanged ? (
                <>
                    <h2>Tu contraseña ha expirado. Por favor, selecciona una nueva para acceder a tu cuenta.</h2>
                    <p className="my-3 text-center">La contraseña debe tener al menos 8 caracteres, debe incluir mayúsculas, minúsculas y números o símbolos ($, !, %, etc.)</p>
                    <div className="box">
                        <div className="input-group">
                            <input
                                type={visiblePassword ? "text" : "password"}
                                id="password"
                                name="password"
                                className="contrasena"
                                placeholder={translate.NewPassword}
                                onChange={handleInputChange}
                            />
                            <i
                                onClick={handleVisiblePassword}
                                className={`fas fa-${visiblePassword ? "un" : ""}lock-alt icono-input`}
                            />
                        </div>
                        <ValidationMessage defaultValue={formData?.password} onChange={requiredManager.getValidation("password")} />
                        <div className="login-btn" id="loginbut">
                            <button type="submit" onClick={submit} className="botondemo" disabled={loading}>
                                {TranslationService.translate.Save.toUpperCase()}
                                {loading && <i className="fas fa-spinner-third fa-spin third ms-2"></i>}
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <h5 style={{ textAlign: "justify" }}>{passwordMsgBlocker}</h5>
                    <p style={{ marginBottom: 15 }}></p>
                </>
            )}
        </form>
    );
};